import React from 'react';
import {
    Box, Chip, CircularProgress, Typography, Alert, Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import useFetch from '../../../../hooks/useFetch';
import { API } from '../../../../utils/apiRoutes';
import { toPersianDigits } from '../../../../utils/convertNumbers';

const StyledChip = styled(Chip)(({ theme, $available, $isSelected }) => ({
    margin: theme.spacing(0.5),
    cursor: $available ? 'pointer' : 'default',
    opacity: $available ? 1 : 0.6,
    '&:hover': {
        backgroundColor: $available ? theme.palette.primary.light : 'inherit',
        transform: $available ? 'scale(1.05)' : 'none',
    },
    transition: 'all 0.2s ease-in-out',
    ...($isSelected && {
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: theme.palette.primary.main,
    }),
}));

const SlotWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: theme.spacing(0.5),
}));

const SlotDescription = styled(Typography)(({ theme }) => ({
    fontSize: '0.7rem',
    marginTop: theme.spacing(0.25),
    color: theme.palette.text.secondary,
    textAlign: 'center',
    maxWidth: '120px',
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    gap: theme.spacing(1),
}));

const SlotsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
}));

const SlotsContainerWrapper = styled(Box)(({ theme }) => ({
    minHeight: theme.spacing(10),
    padding: theme.spacing(1),
}));

// Helper function to format time in Farsi
const formatTimeInFarsi = (timeString) => {
    if (!timeString) return '';
    return toPersianDigits(timeString);
};

// Helper function to format course info like dropdown: "course_name - teacherName - duration - (courseType)"
// For teacher conflicts, exclude teacher name since it's already known
const formatCourseInfo = (course, includeTeacher = true) => {
    if (!course) return '';
    
    const courseName = course.course_name || '';
    const teacherName = course.teacher 
        ? `${course.teacher.first_name || ''} ${course.teacher.last_name || ''}`.trim() 
        : 'بدون آموزگار';
    const duration = course.duration ? `${toPersianDigits(course.duration)} دقیقه` : '';
    const courseTypeLabel = course.course_type === 'individual' ? 'خصوصی' : 'گروهی';
    
    const parts = [courseName];
    if (includeTeacher && teacherName !== 'بدون آموزگار') {
        parts.push(teacherName);
    }
    if (duration) {
        parts.push(duration);
    }
    if (course.course_type) {
        parts.push(`(${courseTypeLabel})`);
    }
    return parts.join(' - ');
};

// Helper function to build conflict display text with actual times
const buildConflictText = (blocker, reason) => {
    const startTime = blocker.start_time ? formatTimeInFarsi(blocker.start_time) : '';
    const endTime = blocker.end_time ? formatTimeInFarsi(blocker.end_time) : '';
    const timeRange = startTime && endTime ? ` (${startTime} - ${endTime})` : '';
    
    if (reason === 'student_conflict') {
        // For student conflicts, show course info (no need to repeat student name)
        const courseInfo = formatCourseInfo(blocker.course);
        // Note: We'll check if it's the current course in the component and adjust description accordingly
        return courseInfo ? `${courseInfo}${timeRange}` : `دوره${timeRange}`;
    } else if (reason === 'teacher_conflict') {
        // For teacher conflicts, show course info without teacher name (teacher is already known) and student name
        const courseInfo = formatCourseInfo(blocker.course, false); // Exclude teacher name
        const studentName = blocker.student ? 
            `${blocker.student.first_name || ''} ${blocker.student.last_name || ''}`.trim() : 
            '';
        if (studentName && courseInfo) {
            return `${courseInfo} - هنرجو: ${studentName}${timeRange}`;
        } else if (courseInfo) {
            return `${courseInfo}${timeRange}`;
        }
        return `تداخل${timeRange}`;
    } else if (reason === 'capacity_full') {
        const courseInfo = formatCourseInfo(blocker.course);
        return courseInfo ? `ظرفیت تکمیل - ${courseInfo}${timeRange}` : `ظرفیت تکمیل${timeRange}`;
    }
    
    return `تداخل${timeRange}`;
};

export default function SlotTags({ 
    studentId, 
    teacherCourseId, 
    day, 
    selectedTime,
    onSelect,
    enabled = false,
    refreshKey = 0,  // Key to force refetch when it changes
    editRecord = null,  // Current registration being edited
    mode = 'create'  // 'create' or 'edit'
}) {
    // Get current course ID for comparison
    const currentCourseId = teacherCourseId;
    // Build query parameters
    const queryParams = React.useMemo(() => {
        const params = new URLSearchParams();
        
        if (teacherCourseId) {
            params.append('teacher_course_id', teacherCourseId.toString());
        }
        
        if (day !== undefined && day !== '') {
            params.append('day', day.toString());
        }
        
        if (studentId) {
            params.append('student_id', studentId.toString());
        }
        
        // Include refreshKey to force refetch when it changes (even though API ignores it)
        if (refreshKey > 0) {
            params.append('_refresh', refreshKey.toString());
        }
        
        return params;
    }, [teacherCourseId, day, studentId, refreshKey]);
    
    // Only fetch when enabled and all required params are present
    const shouldFetch = enabled && teacherCourseId && day !== undefined && day !== '';
    const url = shouldFetch ? `${API.availableSlots}?${queryParams.toString()}` : null;
    
    const { data, loading, error } = useFetch(url);
    
    // Always render wrapper, show helper text when not enabled
    if (!enabled) {
        return (
            <SlotsContainerWrapper>
                <Typography align="center" variant="body2" color="text.secondary">
                    ابتدا روز را انتخاب کنید
                </Typography>
            </SlotsContainerWrapper>
        );
    }
    
    // Loading state
    if (loading) {
        return (
            <SlotsContainerWrapper>
                <Typography align="center" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    در حال بررسی تداخلات...
                </Typography>
                <LoadingContainer>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                        بارگذاری...
                    </Typography>
                </LoadingContainer>
            </SlotsContainerWrapper>
        );
    }
    
    // Error state
    if (error) {
        return (
            <SlotsContainerWrapper>
                <Alert severity="error" sx={{ fontSize: '0.875rem' }}>
                    خطا در بررسی تداخلات: {error}
                </Alert>
            </SlotsContainerWrapper>
        );
    }
    
    // No data or empty slots
    if (!data || !data.slots || data.slots.length === 0) {
        return (
            <SlotsContainerWrapper>
                <Typography align="center" variant="body2" color="text.secondary">
                    تداخلی یافت نشد.
                </Typography>
            </SlotsContainerWrapper>
        );
    }
    
    const { slots } = data;
    
    // Filter to show only conflicting slots (student_conflict or teacher_conflict)
    const conflictSlots = slots.filter(slot => 
        !slot.available && 
        (slot.reason === 'student_conflict' || slot.reason === 'teacher_conflict') &&
        Array.isArray(slot.blockers) && 
        slot.blockers.length > 0
    );
    
    // If no conflicts found
    if (conflictSlots.length === 0) {
        return (
            <SlotsContainerWrapper>
                <Typography align="center" variant="body2" color="text.secondary">
                    تداخلی برای این روز یافت نشد.
                </Typography>
            </SlotsContainerWrapper>
        );
    }
    
    // Group conflicts by type and extract unique conflicts from blockers
    const studentConflicts = [];
    const teacherConflicts = [];
    
    conflictSlots.forEach(slot => {
        if (slot.reason === 'student_conflict' && slot.blockers) {
            slot.blockers.forEach(blocker => {
                if (blocker.start_time && blocker.end_time) {
                    studentConflicts.push({
                        start_time: blocker.start_time,
                        end_time: blocker.end_time,
                        blocker: blocker,
                        reason: 'student_conflict'
                    });
                }
            });
        } else if (slot.reason === 'teacher_conflict' && slot.blockers) {
            slot.blockers.forEach(blocker => {
                if (blocker.start_time && blocker.end_time) {
                    teacherConflicts.push({
                        start_time: blocker.start_time,
                        end_time: blocker.end_time,
                        blocker: blocker,
                        reason: 'teacher_conflict'
                    });
                }
            });
        }
    });
    
    // Remove duplicates based on start_time and end_time
    const uniqueStudentConflicts = Array.from(
        new Map(studentConflicts.map(conflict => [
            `${conflict.start_time}-${conflict.end_time}`,
            conflict
        ])).values()
    );
    
    const uniqueTeacherConflicts = Array.from(
        new Map(teacherConflicts.map(conflict => [
            `${conflict.start_time}-${conflict.end_time}`,
            conflict
        ])).values()
    );
    
    return (
        <SlotsContainerWrapper>
            {uniqueStudentConflicts.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="error" sx={{ mb: 1, fontWeight: 'bold' }}>
                        تداخل با برنامه هنرجو:
                    </Typography>
                    <SlotsContainer>
                        {uniqueStudentConflicts.map((conflict, index) => {
                            const chipLabel = `${formatTimeInFarsi(conflict.start_time)} - ${formatTimeInFarsi(conflict.end_time)}`;
                            
                            // Check if conflict is with the same course (current registration)
                            const conflictingCourseId = conflict.blocker?.course?.id;
                            const isCurrentCourse = currentCourseId && conflictingCourseId && 
                                (Number(currentCourseId) === Number(conflictingCourseId));
                            
                            // Build conflict text - if it's the current course, show a more specific message
                            let conflictText;
                            if (isCurrentCourse) {
                                const startTime = conflict.start_time ? formatTimeInFarsi(conflict.start_time) : '';
                                const endTime = conflict.end_time ? formatTimeInFarsi(conflict.end_time) : '';
                                const timeRange = startTime && endTime ? ` (${startTime} - ${endTime})` : '';
                                conflictText = `ثبت نام فعلی هنرجو در این دوره${timeRange}`;
                            } else {
                                conflictText = buildConflictText(conflict.blocker, 'student_conflict');
                            }
                            
                            const descriptionText = isCurrentCourse 
                                ? 'ثبت نام فعلی هنرجو' 
                                : 'هنرجو مشغول است';
                            
                            return (
                                <Tooltip key={`student-${index}`} title={conflictText} arrow>
                                    <SlotWrapper>
                                        <StyledChip
                                            label={chipLabel}
                                            variant="filled"
                                            color="error"
                                            $available={false}
                                            $isSelected={false}
                                            disabled={true}
                                        />
                                        <SlotDescription variant="caption">
                                            {descriptionText}
                                        </SlotDescription>
                                    </SlotWrapper>
                                </Tooltip>
                            );
                        })}
                    </SlotsContainer>
                </Box>
            )}
            
            {uniqueTeacherConflicts.length > 0 && (
                <Box>
                    <Typography variant="body2" color="warning.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                        تداخل با برنامه آموزگار:
                    </Typography>
                    <SlotsContainer>
                        {uniqueTeacherConflicts.map((conflict, index) => {
                            const chipLabel = `${formatTimeInFarsi(conflict.start_time)} - ${formatTimeInFarsi(conflict.end_time)}`;
                            const conflictText = buildConflictText(conflict.blocker, 'teacher_conflict');
                            
                            return (
                                <Tooltip key={`teacher-${index}`} title={conflictText} arrow>
                                    <SlotWrapper>
                                        <StyledChip
                                            label={chipLabel}
                                            variant="filled"
                                            color="warning"
                                            $available={false}
                                            $isSelected={false}
                                            disabled={true}
                                        />
                                        <SlotDescription variant="caption">
                                            آموزگار مشغول است
                                        </SlotDescription>
                                    </SlotWrapper>
                                </Tooltip>
                            );
                        })}
                    </SlotsContainer>
                </Box>
            )}
        </SlotsContainerWrapper>
    );
}
